import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

interface UseStickyAutoScrollOptions {
  dependencyKey: string | number;
  enabled?: boolean;
  threshold?: number;
}

interface UseStickyAutoScrollResult<T extends HTMLElement> {
  containerRef: MutableRefObject<T | null>;
  isPinnedToBottom: boolean;
  hasDetachedUpdates: boolean;
  scrollToBottom: () => void;
  pinNextUpdate: () => void;
}

const DEFAULT_THRESHOLD = 72;

function getDistanceFromBottom(element: HTMLElement): number {
  return element.scrollHeight - element.scrollTop - element.clientHeight;
}

export function useStickyAutoScroll<T extends HTMLElement>({
  dependencyKey,
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
}: UseStickyAutoScrollOptions): UseStickyAutoScrollResult<T> {
  const containerRef = useRef<T | null>(null);
  const pinnedRef = useRef(true);
  const pinNextUpdateRef = useRef(true);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const [hasDetachedUpdates, setHasDetachedUpdates] = useState(false);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
    pinnedRef.current = true;
    pinNextUpdateRef.current = true;
    setIsPinnedToBottom(true);
    setHasDetachedUpdates(false);
  }, []);

  const pinNextUpdate = useCallback(() => {
    pinNextUpdateRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const isPinned = getDistanceFromBottom(container) <= threshold;
      pinnedRef.current = isPinned;
      setIsPinnedToBottom(isPinned);

      if (isPinned) {
        setHasDetachedUpdates(false);
      }
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, threshold]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const container = containerRef.current;

    if (!container) {
      return;
    }

    if (pinNextUpdateRef.current || pinnedRef.current) {
      pinNextUpdateRef.current = false;
      scrollToBottom();
      return;
    }

    setHasDetachedUpdates(true);
  }, [dependencyKey, enabled, scrollToBottom]);

  return {
    containerRef,
    isPinnedToBottom,
    hasDetachedUpdates,
    scrollToBottom,
    pinNextUpdate,
  };
}
